import { useState, useEffect } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { SectionCard, Empty } from "../components/shared";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trophy, GitCompareArrows, ArrowRight, CalendarDays } from "lucide-react";
import DateFilter, { resolveRange } from "../components/DateFilter";

const blankVendor = () => ({ name: "", contact: "", phone: "", unit_price: 0, payment_terms: "", lead_time_days: 0 });
const emptyForm = { item_name: "", category: "Consumables", branch: "Sarita Vihar", unit: "Piece", qty: 1, remarks: "", vendors: [blankVendor(), blankVendor(), blankVendor()] };

export default function Quotations() {
  const { meta } = useApp();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [preset, setPreset] = useState("All");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const range = resolveRange(preset, customFrom, customTo);
  const filtered = rows.filter((q) => {
    const d = q.quotation_date || "";
    if (range.from && d < range.from) return false;
    if (range.to && d > range.to) return false;
    return true;
  });
  const byDate = filtered.reduce((acc, q) => {
    const d = q.quotation_date || "Unknown";
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});
  const dateChips = Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0]));

  const load = () => api.get("/quotations").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const setV = (i, k, val) => setForm((f) => {
    const vendors = [...f.vendors]; vendors[i] = { ...vendors[i], [k]: val }; return { ...f, vendors };
  });

  const prices = form.vendors.map((v) => Number(v.unit_price) || Infinity);
  const bestIdx = prices.every((p) => p === Infinity) ? -1 : prices.indexOf(Math.min(...prices));

  const submit = async () => {
    if (!form.item_name) { toast.error("Item name required"); return; }
    const vendors = form.vendors.filter((v) => v.name).map((v) => ({ ...v, unit_price: Number(v.unit_price), lead_time_days: Number(v.lead_time_days) }));
    if (vendors.length < 2) { toast.error("Add at least 2 vendor quotes"); return; }
    await api.post("/quotations", { ...form, qty: Number(form.qty), vendors });
    toast.success("Quotation comparison saved"); setOpen(false); setForm(emptyForm); load();
  };

  const convert = async (id) => {
    await api.post(`/quotations/${id}/convert`);
    toast.success("Best vendor converted to Purchase Order (see Procurement Log)");
    load();
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">3-Vendor Quotation & Comparison</h1>
          <p className="text-brand-slate text-sm mt-1">Compare quotes side-by-side, auto-highlight best value & convert to PO</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-quote-button" className="gap-2 bg-brand-teal hover:bg-brand-tealdark active:scale-95 transition-transform"><Plus className="w-4 h-4" /> New Comparison</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Compare & Finalize Vendor</DialogTitle>
              <DialogDescription>Enter up to three vendor quotes; the lowest price is auto-highlighted as best value.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2"><Label>Item</Label><Input data-testid="quote-item" value={form.item_name} onChange={(e) => setForm((f) => ({ ...f, item_name: e.target.value }))} /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Qty</Label><Input type="number" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              {form.vendors.map((v, i) => (
                <div key={i} data-testid={`quote-vendor-${i}`} className={`rounded-xl border-2 p-3 space-y-2 ${bestIdx === i ? "border-brand-teal bg-teal-50" : "border-border"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-slate">Vendor {i + 1}</span>
                    {bestIdx === i && <span className="text-[10px] font-bold text-white bg-brand-teal px-2 py-0.5 rounded-full flex items-center gap-1"><Trophy className="w-3 h-3" /> Best</span>}
                  </div>
                  <Input placeholder="Name" value={v.name} onChange={(e) => setV(i, "name", e.target.value)} />
                  <Input placeholder="Contact person" value={v.contact} onChange={(e) => setV(i, "contact", e.target.value)} />
                  <Input placeholder="Phone" value={v.phone} onChange={(e) => setV(i, "phone", e.target.value)} />
                  <Input type="number" placeholder="Price / unit (₹)" value={v.unit_price} onChange={(e) => setV(i, "unit_price", e.target.value)} />
                  <Input placeholder="Payment terms" value={v.payment_terms} onChange={(e) => setV(i, "payment_terms", e.target.value)} />
                  <Input type="number" placeholder="Lead time (days)" value={v.lead_time_days} onChange={(e) => setV(i, "lead_time_days", e.target.value)} />
                  {Number(v.unit_price) > 0 && <div className="text-xs text-brand-slate">Total: <span className="font-semibold text-navy">{inr(Number(v.unit_price) * Number(form.qty || 1))}</span></div>}
                </div>
              ))}
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="quote-submit" onClick={submit} className="bg-brand-teal hover:bg-brand-tealdark">Save Comparison</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <SectionCard className="!py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <DateFilter value={preset} onChange={setPreset} customFrom={customFrom} customTo={customTo} setCustomFrom={setCustomFrom} setCustomTo={setCustomTo} />
          <span data-testid="quotes-count" className="text-sm font-semibold text-navy">{filtered.length} quotation{filtered.length === 1 ? "" : "s"}</span>
        </div>
        {dateChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="quotes-by-date">
            <span className="text-xs font-semibold text-brand-slate flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Taken on:</span>
            {dateChips.map(([d, n]) => (
              <span key={d} data-testid={`quote-date-chip-${d}`} className="text-xs font-semibold bg-teal-50 text-brand-tealdark border border-brand-teal/30 px-2.5 py-1 rounded-full">
                {d} · {n} quote{n === 1 ? "" : "s"}
              </span>
            ))}
          </div>
        )}
      </SectionCard>

      {filtered.length === 0 ? <SectionCard><Empty icon={GitCompareArrows} msg={rows.length === 0 ? "No quotation comparisons yet" : "No quotations in the selected date range"} /></SectionCard> :
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {filtered.map((q) => (
            <div key={q.id} data-testid="quote-card" className="bg-white rounded-xl border border-border p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-navy">{q.item_name}</h3>
                  <p className="text-xs text-brand-slate mt-0.5">{q.category} · {q.branch} · Qty {q.qty}</p>
                  <p data-testid="quote-date" className="text-[11px] text-brand-slate mt-0.5 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Quoted on {q.quotation_date || "—"}</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-brand-slate">Potential saving</div>
                  <div className="font-heading font-bold text-brand-emerald">{inr(q.potential_savings)}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                {q.vendors.map((v, i) => {
                  const best = v.name === q.best_vendor;
                  return (
                    <div key={i} className={`rounded-lg border p-2.5 ${best ? "border-brand-teal bg-teal-50" : "border-border"}`}>
                      <div className="flex items-center gap-1 text-xs font-semibold text-navy truncate">{best && <Trophy className="w-3 h-3 text-brand-teal shrink-0" />} {v.name}</div>
                      <div className="font-heading font-bold text-navy mt-1">{inr(v.unit_price)}</div>
                      <div className="text-[11px] text-brand-slate mt-1">{v.payment_terms || "—"}</div>
                      <div className="text-[11px] text-brand-slate">{v.lead_time_days}d lead</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                <span className="text-sm text-brand-slate">Best value: <span className="font-semibold text-navy">{q.best_vendor}</span></span>
                <Button data-testid="convert-quote" size="sm" onClick={() => convert(q.id)} className="gap-1.5 bg-navy hover:bg-navy/90 h-8">
                  Convert to PO <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}
